import type { VatLevel } from "~/store/shared"
import { EVENTS, track, moneyGbp, numeric, entityId, enumValue } from "~/utils/analytics"
import { ALLERGEN_FLAG_CONFLICTS } from "~/utils/allergenFlagConflicts"

export const ProductTypes = Object.freeze({
	Inventory: "Inventory",
	Bundle: "Bundle",
})

export type ProductType = (typeof ProductTypes)[keyof typeof ProductTypes]

export const UnifiedCategories = Object.freeze({
	IndividualMeals: "Individual Meals",
	SharingMains: "Sharing Mains",
	SharingSideAndSalads: "Sharing Side & Salads",
	SharingHandhelds: "Sharing Handhelds",
	SharingSnacksAndFingerFood: "Sharing Snacks and Finger Food",
	SharingDesserts: "Sharing Desserts",
	SharingBreakfast: "Sharing Breakfast",
	IndividualHandhelds: "Individual Handhelds",
	IndividualSnacksAndFingerFood: "Individual Snacks and Finger Food",
	IndividualDesserts: "Individual Desserts",
	IndividualBreakfast: "Individual Breakfast",
	Canapes: "Canapes",
	Drinks: "Drinks",
	SaucesAndToppings: "Sauces & Toppings",
	Confectionary: "Confectionary",
	Extras: "Extras",
	SeasonalEvent: "Seasonal Event",
})

export type UnifiedCategory = (typeof UnifiedCategories)[keyof typeof UnifiedCategories]

export interface Product {
	id: number | null
	is_active: boolean
	name: string
	same_day?: boolean
	future_day?: boolean
	individually_packaged?: boolean
	pieces?: number
	min_order_amount?: number
	is_vegetarian?: boolean
	is_vegan?: boolean
	is_halal?: boolean
	is_gluten_free?: boolean
	is_nut_free?: boolean
	is_dairy_free?: boolean
	no_known_allergens?: boolean
	no_dietary_claims?: boolean
	is_vatable: boolean
	servings?: number
	stock_count?: number
	stock_remaining?: number
	price_net: number
	price_vat: number
	price_gross: number
	description: string
	origin_id?: string
	maker_id: string
	attachments?: Record<string, unknown>[]
	vat_level: VatLevel
	hubspot_id?: string
	type: ProductType
	is_premium: boolean
	premium_name?: string
	unified_category?: UnifiedCategory
	product_category_id: number
	modifiers: any[]
	modifierGroups: any[]
	deletedModifierGroupIds?: number[]
}

export interface ProductState {
	products: Product[]
	chosenProduct: Product | false
	originalModifierGroups: string | null
	originalAllergens: string | null
	originalTags: string | null
	originalProductCategories: string | null
	originalProductAttributes: string | null
	productsLoading: boolean
	productLoading: boolean
	productSaving: boolean
	productError: string
}

export const VatLevels = Object.freeze({
	"20": "20",
	"0": "0",
})

export type VatLevel = (typeof VatLevels)[keyof typeof VatLevels]

const sortById = <T extends { id?: number | null }>(arr: T[]): T[] =>
	[...arr].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))

const productStoreModule = {
	namespaced: true,
	state: (): ProductState => ({
		products: [],
		chosenProduct: false,
		originalModifierGroups: null,
		originalAllergens: null,
		originalTags: null,
		originalProductCategories: null,
		originalProductAttributes: null,
		productsLoading: false,
		productLoading: false,
		productSaving: false,
		productError: "",
	}),
	mutations: {
		SET_PRODUCTS(state: ProductState, payload: Product[]) {
			state.products = payload
		},
		SET_CHOSEN_PRODUCT(state: ProductState, payload: Product) {
			const mappedGroups = (payload.modifierGroups ?? []).map((group: any) => ({
				...group,
				deletedModifierIds: group.deletedModifierIds ?? [],
			}))
			state.chosenProduct = {
				...payload,
				modifierGroups: mappedGroups,
				deletedModifierGroupIds: [],
			}
			state.originalModifierGroups = JSON.stringify(
				sortById(mappedGroups).map((g: any) => ({ ...g, modifiers: sortById(g.modifiers ?? []) }))
			)
			state.originalAllergens = JSON.stringify(sortById((payload as any).allergens ?? []))
			state.originalTags = JSON.stringify(sortById((payload as any).tags ?? []))
			state.originalProductCategories = JSON.stringify(
				sortById((payload as any).productCategories ?? [])
			)
			state.originalProductAttributes = JSON.stringify(
				sortById((payload as any).productAttributes ?? [])
			)
		},
		SET_NEW_GROUP(state: ProductState, payload: any) {
			state.chosenProduct?.modifierGroups.push(payload)
		},
		SET_NEW_MODIFIER(state: ProductState, payload: any) {
			state.chosenProduct?.modifierGroups[payload.index].modifiers.push(payload.body)
		},
		SET_PRODUCTS_LOADING(state: ProductState, payload: boolean) {
			state.productsLoading = payload
		},
		SET_PRODUCT_LOADING(state: ProductState, payload: boolean) {
			state.productLoading = payload
		},
		SET_PRODUCT_SAVING(state: ProductState, payload: boolean) {
			state.productSaving = payload
		},
		TRACK_DELETED_MODIFIER(
			state: ProductState,
			payload: { groupIndex: number; modifierId: number }
		) {
			if (!state.chosenProduct) return
			const group = state.chosenProduct.modifierGroups[payload.groupIndex]
			if (!group) return
			if (!group.deletedModifierIds) group.deletedModifierIds = []
			group.deletedModifierIds.push(payload.modifierId)
		},
		TRACK_DELETED_MODIFIER_GROUP(state: ProductState, payload: { groupId: number }) {
			if (!state.chosenProduct) return
			if (!state.chosenProduct.deletedModifierGroupIds)
				state.chosenProduct.deletedModifierGroupIds = []
			state.chosenProduct.deletedModifierGroupIds.push(payload.groupId)
		},
		UPDATE_CHOSEN_PRODUCT(state: ProductState, payload: Product) {
			const mappedGroups = (payload.modifierGroups ?? []).map((group: any) => ({
				...group,
				deletedModifierIds: group.deletedModifierIds ?? [],
			}))
			state.chosenProduct = {
				...payload,
				modifierGroups: mappedGroups,
			}
		},
		SET_PRODUCT_ERROR(state: ProductState, payload: string) {
			state.productError = payload
		},
	},
	actions: {
		async calculatePrices(
			{ dispatch, commit },
			payload: { price_net: number; vat_level: VatLevel; is_vatable: boolean }
		) {
			const body = JSON.stringify(payload)
			const config = {
				headers: {
					accept: "application/json",
					"content-type": "application/json",
				},
				method: "POST",
				body,
			}

			return await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/public/products/calculatePrices`,
				config
			).catch((error) => {
				dispatch("handleError", { error, description: "error calculating prices" }, { root: true })
				commit("SET_PRODUCT_ERROR", "An error occurred while calculating prices")

				throw error
			})
		},
		getProducts: debounce(async function ({ commit, dispatch }, payload) {
			const filters = payload?.filters || []
			const page = payload?.page || 1
			const limit = payload?.limit || 20
			const orderBy = payload?.orderBy || "createdAt"
			const orderDirection = payload?.orderDirection || "DESC"
			!payload && commit("SET_PRODUCTS_LOADING", true)

			const queryParams = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				orderBy,
				orderDirection,
			}).toString()

			const productCategoryIds = payload?.productCategoryIds ?? []
			const config = {
				method: "POST",
				body: JSON.stringify({
					filters,
					productCategoryIds,
				}),
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/products/filter?${queryParams}`,
				config
			)
				.then((response) => {
					const res = response as { data: any; paginate: any }
					commit("SET_PRODUCTS", res.data)
					commit("SET_PAGES", res.paginate, { root: true })
					commit("SET_PRODUCTS_LOADING", false)
				})
				.catch((error) => {
					commit("SET_PRODUCTS_LOADING", false)
					// dispatch root action for centralized error handling
					dispatch("handleError", { error }, { root: true })
				})
		}, 200),
		async getProductById({ commit, dispatch }, id) {
			commit("SET_PRODUCT_LOADING", true)
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}

			// Resolves to the product, or undefined when the read failed (the error is already handled).
			return $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/products/${id}`, config)
				.then((response) => {
					commit("SET_CHOSEN_PRODUCT", response)
					commit("SET_PRODUCT_LOADING", false)
					return response
				})
				.catch((error) => {
					commit("SET_PRODUCT_LOADING", false)
					dispatch("handleError", { error }, { root: true })
					return undefined
				})
		},
		async findProductByName({ dispatch }, { makerId, name }: { makerId: string; name: string }) {
			const config = {
				method: "POST",
				body: JSON.stringify({
					filters: { maker_id: makerId, name: { "==": name } },
				}),
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
			}
			try {
				const response = (await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/products/filter?limit=1`,
					config
				)) as { data: any[] }
				return response.data?.[0] ?? null
			} catch (error) {
				dispatch("handleError", { error }, { root: true })
				return null
			}
		},
		async createProduct({ state, commit, dispatch }) {
			const body = JSON.stringify({
				...state.chosenProduct,
				// just so we are sure it's a string
				vat_level: state.chosenProduct.vat_level.toString(),
			})
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "POST",
				body,
			}

			commit("SET_PRODUCT_ERROR", "")

			await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/products`, config)
				.then((response) => {
					track(EVENTS.PRODUCT_SAVED, {
						operation: "created",
						product_id: entityId(response?.id),
						maker_id: entityId(state.chosenProduct?.maker_id),
						price_net_gbp: moneyGbp(state.chosenProduct?.price_net),
						vat_level: enumValue(state.chosenProduct?.vat_level),
						is_vatable: Boolean(state.chosenProduct?.is_vatable),
						// The declarations as created: the server can drop or normalise them.
						no_known_allergens: Boolean(response?.no_known_allergens),
						no_dietary_claims: Boolean(response?.no_dietary_claims),
						modifier_group_count: Array.isArray(state.chosenProduct?.modifierGroups)
							? state.chosenProduct.modifierGroups.length
							: undefined,
					})

					dispatch("getProducts")

					navigateTo(`/products/${response.id}`)
				})
				.catch((error) => {
					dispatch("handleError", { error, description: "create product" }, { root: true })
					if (error?.response?.status === 400) {
						commit("SET_PRODUCT_ERROR", "Product name already in use")
					} else {
						commit("SET_PRODUCT_ERROR", "An error occurred while creating the product")
					}
					throw error
				})
		},
		async updateProduct({ state, commit, dispatch }) {
			if (!state.chosenProduct || state.productSaving) return

			const chosenProduct = state.chosenProduct as any

			const modifierGroupsChanged =
				JSON.stringify(
					sortById(state.chosenProduct.modifierGroups).map((g: any) => ({
						...g,
						modifiers: sortById(g.modifiers ?? []),
					}))
				) !== state.originalModifierGroups
			const allergensChanged =
				JSON.stringify(sortById(chosenProduct.allergens ?? [])) !== state.originalAllergens
			const tagsChanged = JSON.stringify(sortById(chosenProduct.tags ?? [])) !== state.originalTags
			const productCategoriesChanged =
				JSON.stringify(sortById(chosenProduct.productCategories ?? [])) !==
				state.originalProductCategories
			const productAttributesChanged =
				JSON.stringify(sortById(chosenProduct.productAttributes ?? [])) !==
				state.originalProductAttributes

			const deletedModifierGroupIds = state.chosenProduct.deletedModifierGroupIds ?? []
			// A group removal must always reach the backend, even if the remaining
			// groups serialise identically to the snapshot.
			const modifierGroupsChangedOrDeleted =
				modifierGroupsChanged || deletedModifierGroupIds.length > 0

			const body = JSON.stringify({
				...state.chosenProduct,
				// just so we are sure it's a string
				vat_level: state.chosenProduct.vat_level.toString(),
				maker_id: state.chosenProduct.maker_id,
				// undefined is omitted by JSON.stringify — backend skips the section when absent
				allergens: allergensChanged ? chosenProduct.allergens : undefined,
				tags: tagsChanged ? chosenProduct.tags : undefined,
				productCategories: productCategoriesChanged ? chosenProduct.productCategories : undefined,
				productAttributes: productAttributesChanged ? chosenProduct.productAttributes : undefined,
				modifierGroups: modifierGroupsChangedOrDeleted
					? state.chosenProduct.modifierGroups.map((group) => ({
							...group,
							deletedModifierIds: group.deletedModifierIds ?? [],
							modifiers: group.modifiers.map((modifier) => ({
								...modifier,
								price_net: parseInt(modifier.price_net),
								vat_level: state.chosenProduct.vat_level.toString(),
								is_vatable: state.chosenProduct.is_vatable,
							})),
						}))
					: undefined,
				deletedModifierGroupIds: deletedModifierGroupIds.length
					? deletedModifierGroupIds
					: undefined,
			})
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "put",
				body,
			}

			commit("SET_PRODUCT_SAVING", true)
			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/products/${state.chosenProduct.id}`,
				config
			)
				.then(async () => {
					// Refresh from API so newly created modifiers get their backend IDs and snapshots reset
					// correctly. getProductById handles its own error and resolves undefined.
					const refreshed = await dispatch("getProductById", state.chosenProduct.id)

					// The declarations as saved, since the server can drop or normalise them (or as
					// sent, if the refresh failed). The rest describes what the admin sent.
					const saved = refreshed || chosenProduct
					track(EVENTS.PRODUCT_SAVED, {
						operation: "updated",
						product_id: entityId(chosenProduct?.id),
						maker_id: entityId(chosenProduct?.maker_id),
						price_net_gbp: moneyGbp(chosenProduct?.price_net),
						vat_level: enumValue(chosenProduct?.vat_level),
						is_vatable: Boolean(chosenProduct?.is_vatable),
						no_known_allergens: Boolean(saved?.no_known_allergens),
						no_dietary_claims: Boolean(saved?.no_dietary_claims),
						modifier_group_count: Array.isArray(chosenProduct?.modifierGroups)
							? chosenProduct.modifierGroups.length
							: undefined,
						// Which sections of the form get used, so we know what to
						// simplify — reuses the flags the action already computed.
						changed_sections: [
							modifierGroupsChanged && "modifier_groups",
							allergensChanged && "allergens",
							tagsChanged && "tags",
							productCategoriesChanged && "product_categories",
							productAttributesChanged && "product_attributes",
						].filter(Boolean),
						deleted_modifier_group_count: deletedModifierGroupIds.length,
					})
					commit("SET_PRODUCT_SAVING", false)
					commit("SET_SUCCESS_MESSAGE", "Successfully updated product", { root: true })
					setTimeout(() => {
						commit("SET_SUCCESS_MESSAGE", false, { root: true })
					}, 5000)
				})
				.catch((error) => {
					commit("SET_PRODUCT_SAVING", false)
					dispatch("handleError", { error, description: "updated product" }, { root: true })
				})
		},
		// Products export is now generated asynchronously via the file-generation
		// queue; the backend emails a download link when ready.
		async downloadCSV({ dispatch, commit }) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
				},
			}

			await $fetch(`${import.meta.env.VITE_AND_DINE_API}/api/users/products/exportCsv`, config)
				.then(() => {
					commit(
						"SET_SUCCESS_MESSAGE",
						"Report queued — we'll email you a download link when it's ready.",
						{ root: true }
					)
					setTimeout(() => commit("SET_SUCCESS_MESSAGE", false, { root: true }), 5000)
				})
				.catch((error) => {
					dispatch("handleError", { error }, { root: true })
				})
		},
		async duplicateProduct({ state, commit, dispatch }) {
			const body = JSON.stringify({
				...state.chosenProduct,
			})
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "POST",
				body,
			}

			await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/products/clone/${state.chosenProduct.id}`,
				config
			)
				.then((response) => {
					// Clone-vs-create ratio is the strongest signal for whether a
					// "product template" feature is worth building.
					track(EVENTS.PRODUCT_DUPLICATED, {
						source_product_id: entityId(state.chosenProduct?.id),
						new_product_id: entityId(response?.id),
						maker_id: entityId(state.chosenProduct?.maker_id),
					})
					commit("SET_SUCCESS_MESSAGE", "Successfully cloned product", { root: true })
					setTimeout(() => {
						commit("SET_SUCCESS_MESSAGE", false, { root: true })
					}, 5000)
					navigateTo(`/products/${response.id}`)
				})
				.catch((error) => {
					dispatch("handleError", { error, description: "duplicate product" }, { root: true })
				})
		},
		async uploadProductFromCSVRow(_, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "POST" as const,
				body: JSON.stringify(payload),
			}

			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/products`,
				config
			)
			return response
		},
		async updateProductFromCSVRow(_, payload) {
			const config = {
				headers: {
					accept: "application/json",
					"x-access-token-user": useCookie("auth_token").value ?? "",
					"content-type": "application/json",
				},
				method: "PUT" as const,
				body: JSON.stringify(payload),
			}

			const response = await $fetch(
				`${import.meta.env.VITE_AND_DINE_API}/api/users/products/${payload.id}`,
				config
			)
			return response
		},
	},
	getters: {
		allergenFreeOptions: () => ALLERGEN_FLAG_CONFLICTS,
		productTypeOptions: () => {
			return ["Inventory", "Bundle"].map((status) => ({
				value: status,
				name: status,
			}))
		},
		unifiedCategoriesOptions: () => {
			return [
				"Individual Meals",
				"Sharing Mains",
				"Sharing Side & Salads",
				"Sharing Handhelds",
				"Sharing Snacks and Finger Food",
				"Sharing Desserts",
				"Sharing Breakfast",
				"Individual Handhelds",
				"Individual Snacks and Finger Food",
				"Individual Desserts",
				"Individual Breakfast",
				"Canapes",
				"Drinks",
				"Sauces & Toppings",
				"Confectionary",
				"Extras",
				"Seasonal Event",
			].map((category) => ({
				value: category,
				name: category,
			}))
		},
		vatLevels: () => {
			return Object.values(VatLevels).map((level) => ({
				value: level,
				name: level,
			}))
		},
	},
}

export default productStoreModule
